<template>
  <main class="flex flex-col">
    <header class="flex">
      <h2 id="tabslist" class="sr-only">Status</h2>
      <Tabs :tabs="tabs" @update:currentTab="changeTab" />
      <button
        class="btn"
        :disabled="!sortedNotifications.length"
        @click="readAll()"
      >
        {{ t('buttons.markAllAsRead') }}
      </button>
    </header>
    <div
      v-if="sortedNotifications.length === 0"
      class="flex flex-col justify-center | text-grey-700 | absolute inset-0 -z-1"
    >
      <svg
        aria-hidden="true"
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M23.7 28.925C23.4895 30.6616 22.625 32.2526 21.2825 33.3741C19.94 34.4955 18.2205 35.0631 16.4742 34.9611C14.7279 34.8591 13.0861 34.0953 11.8832 32.8252C10.6804 31.5551 10.0069 29.8743 10 28.125V24.15M34.6 32.675L5.425 22.5C4.94896 22.3339 4.53427 22.0278 4.23534 21.6218C3.93642 21.2158 3.76724 20.7289 3.75 20.225V17.5C3.75206 16.9832 3.91423 16.4798 4.21418 16.0589C4.51414 15.6381 4.93715 15.3205 5.425 15.15L34.6 5.00001C34.7873 4.93673 34.987 4.91879 35.1825 4.94766C35.3781 4.97653 35.5641 5.05137 35.7251 5.16606C35.8862 5.28074 36.0177 5.43199 36.109 5.60738C36.2002 5.78277 36.2485 5.9773 36.25 6.17501V31.5C36.2485 31.6977 36.2002 31.8922 36.109 32.0676C36.0177 32.243 35.8862 32.3943 35.7251 32.5089C35.5641 32.6236 35.3781 32.6985 35.1825 32.7273C34.987 32.7562 34.7873 32.7383 34.6 32.675Z"
          stroke="currentColor"
          stroke-width="1.25"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <p v-if="currentTab === 'all'">
        {{ t('notifications.none') }}
      </p>
      <p v-else>{{ t('notifications.noneUnread') }}</p>
    </div>
    <section v-else class="notifications | flow">
      <template
        v-for="notification in sortedNotifications"
        :key="notification.id"
      >
        <component
          :is="notificationComponents[notification.type]"
          :notification="notification"
          :data-status="notification.isRead ? 'read' : 'unread'"
          @click="handleNotificationClick(notification)"
        />
      </template>
    </section>
  </main>
</template>

<script setup>
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import slugify from 'slugify';
import Tabs from '../components/Tabs.vue';
import { useUserStore } from '../stores/user';
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useApiStore } from '../stores/api';
import Comment from '../components/notifications/Comment.vue';
import Reply from '../components/notifications/Reply.vue';
import Content from '../components/notifications/Content.vue';
import { useRouter } from 'vue-router';
import ProjectRequest from '../components/notifications/ProjectRequest.vue';
import AppointmentRequest from '../components/notifications/AppointmentRequest.vue';
import { useI18n } from 'vue-i18n';

dayjs.locale('fr');

const { t } = useI18n();
const userStore = useUserStore();
const router = useRouter();
const { notifications } = storeToRefs(useUserStore());
const api = useApiStore();
const currentTab = ref('all');
const tabs = computed(() => {
  return [
    {
      label: t('notifications.all'),
      id: 'all',
      icon: null,
      count: null,
      isActive: currentTab.value === 'all',
    },
    {
      label: t('notifications.unread'),
      id: 'unread',
      icon: null,
      count: null,
      isActive: currentTab.value === 'unread',
    },
  ];
});

const notificationComponents = {
  comment: Comment,
  'comment-reply': Reply,
  content: Content,
  'project-request': ProjectRequest,
  'appointment-request': AppointmentRequest,
};

const sortedNotifications = computed(() => {
  const sortedNotifications = [...notifications.value].sort((a, b) => {
    return dayjs(b.date).diff(dayjs(a.date));
  });

  if (currentTab.value === 'unread') {
    return sortedNotifications.filter((notification) => !notification.isRead);
  }
  return sortedNotifications;
});

function changeTab(newValue) {
  currentTab.value = newValue;
}

function readAll() {
  try {
    api.markAllNotificationsRead();
  } catch (error) {
    console.log(t('errors.readAllNotificationsFailed'), error);
  }
}

// Functions
async function handleNotificationClick(notification) {
  // Marquer la notification comme lue
  if (!notification.isRead) {
    try {
      await api.markNotificationRead(notification);
    } catch (error) {
      console.log(t('errors.markNotificationFailed'), error);
    }
  }

  // Naviguer vers la cible
  const href =
    notification.type === 'appointment-request'
      ? getHref(notification) + '?tab=designToLight'
      : getHref(notification);

  if (href.startsWith('http')) {
    window.open(href, '_blank');
  } else {
    router.push(href);
  }
}
function getHref(notification) {
  const uri = notification.location.page.uri;

  // Pour les notifications de type "content" (brief validé), utiliser dialogUri si présent
  if (notification.type === 'content' && notification.dialogUri) {
    return notification.dialogUri;
  }

  const isDocumentBrief =
    notification.location.page.template === 'client-brief' &&
    notification.location?.file?.type === 'document';

  if (isDocumentBrief) {
    return notification.project.uri + '?dialog=client-brief&comments=true';
  }

  if (notification.location.page.template === 'track') {
    const anchor = notification.location.page.title
      ? '#' + slugify(notification?.location?.page?.title, { lower: true })
      : '';
    return (
      notification.project.uri + '?dialog=virtual-sample&comments=true' + anchor
    );
  }

  if (notification.location.page.panelurl) {
    return notification.location.page.panelurl;
  }

  return uri;
}
</script>

<style scoped>
main {
  max-width: 52.5rem;
  margin: auto;
}
main > header {
  flex-wrap: nowrap;
}
main > header [role='tablist'] {
  margin-left: 0;
}
</style>
